import _clamp from 'lodash/clamp';
import { HANDLE_POSITIONS } from '@src/components/wysiwyg_editor/page_content_items/DragHandle';
import type { TRect, TSize } from '@src/types/misc';

type TDimensions = {
  height: number;
  width: number;
};

function nextFrameDimensions(
  startingLocation: TRect,
  movement: Array<number>,
  minimumDimensions: TDimensions,
  maxDimensions: TDimensions
) {
  const heightWidthRatio = startingLocation.height / startingLocation.width;

  let newFrameWidth = _clamp(
    startingLocation.width + movement[0],
    minimumDimensions.width,
    maxDimensions.width
  );

  let newFrameHeight = newFrameWidth * heightWidthRatio;

  if (newFrameHeight > maxDimensions.height) {
    newFrameHeight = maxDimensions.height;
    newFrameWidth = newFrameHeight / heightWidthRatio;
  }

  return {
    height: newFrameHeight,
    width: newFrameWidth
  };
}

function nextFrameLocation(
  position: string,
  initialLocation: TRect,
  newLocation: TSize
) {
  let newX = initialLocation.x;
  let newY = initialLocation.y;

  if (position === HANDLE_POSITIONS.ne || position === HANDLE_POSITIONS.nw) {
    newY = initialLocation.y + initialLocation.height - newLocation.height;
  }

  if (position === HANDLE_POSITIONS.sw || position === HANDLE_POSITIONS.nw) {
    newX = initialLocation.x + initialLocation.width - newLocation.width;
  }

  return { x: newX, y: newY };
}

function calculateMaxDimensions(
  position: string,
  initialLocation: TRect,
  renderableArea: TRect
): TDimensions {
  let height = Infinity;
  let width = Infinity;

  if (position === HANDLE_POSITIONS.ne) {
    height = initialLocation.height + initialLocation.y - renderableArea.y;
    width = renderableArea.width - initialLocation.x + renderableArea.x;
  } else if (position === HANDLE_POSITIONS.nw) {
    height = initialLocation.height + initialLocation.y - renderableArea.y;
    width = initialLocation.x + initialLocation.width - renderableArea.x;
  } else if (position === HANDLE_POSITIONS.se) {
    height = renderableArea.height - initialLocation.y + renderableArea.y;
    width = renderableArea.width - initialLocation.x + renderableArea.x;
  } else if (position === HANDLE_POSITIONS.sw) {
    height = renderableArea.height - initialLocation.y + renderableArea.y;
    width = initialLocation.x + initialLocation.width - renderableArea.x;
  }

  return { height, width };
}

export function handleElementScaling(
  position: string,
  initialFrameLocation: TRect,
  initialContentLocation: TRect | Record<string, never>,
  movement: [number, number],
  renderableArea: TRect,
  minimumDimensions: TDimensions
) {
  const movementVsCenter = [
    movement[0] *
      ([HANDLE_POSITIONS.ne, HANDLE_POSITIONS.se].includes(position) ? 1 : -1),
    movement[1] *
      ([HANDLE_POSITIONS.nw, HANDLE_POSITIONS.ne].includes(position) ? 1 : -1)
  ];
  const newFrameDimensions = nextFrameDimensions(
    initialFrameLocation,
    movementVsCenter,
    minimumDimensions,
    calculateMaxDimensions(position, initialFrameLocation, renderableArea)
  );
  const newFrameLocation = {
    ...newFrameDimensions,
    ...nextFrameLocation(position, initialFrameLocation, newFrameDimensions)
  };
  const newContentLocation = (function (
    initialContentLocation,
    initialFrameLocation,
    finalFrameLocation
  ) {
    const newWidth =
      (finalFrameLocation.width / initialFrameLocation.width) *
      initialContentLocation.width;
    const newHeight =
      (finalFrameLocation.height / initialFrameLocation.height) *
      initialContentLocation.height;
    const newX =
      (finalFrameLocation.width / initialFrameLocation.width) *
      initialContentLocation.x;
    const newY =
      (finalFrameLocation.height / initialFrameLocation.height) *
      initialContentLocation.y;

    return {
      ...initialContentLocation,
      height: newHeight,
      width: newWidth,
      x: newX,
      y: newY
    };
  })(initialContentLocation, initialFrameLocation, newFrameLocation);

  return {
    frame: newFrameLocation,
    content: newContentLocation
  };
}
